import React, { useState, useEffect } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../../service/GetTemplate';
import { endPoints } from '../../../Config';
import { formatTimeFromMinutes } from '../../../utilities/formatTime';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText } from '../../../utilities/siteTranslations';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

const PerformanceReports = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const [satisfactionData, setSatisfactionData] = useState([]);
    const [responseTimes, setResponseTimes] = useState([]);
    const [faultsByTechnician, setFaultsByTechnician] = useState([]);
    const [chartData, setChartData] = useState({});
    const [users, setUsers] = useState([]);
    const [teams, setTeams] = useState([]);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        userId: null,
        teamId: null
    });

    const dispatch = useDispatch();

    useEffect(() => {
        const loadUsers = async () => {
            const response = await handleGetRequest(endPoints.getallUsers, true, false, dispatch);
            if (response?.data) {
                const options = response.data.map((user) => ({
                    label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || t('Unknown User'),
                    value: user._id || user.id
                }));
                setUsers([{ label: t('All Users'), value: null }, ...options]);
            }
        };

        const loadTeams = async () => {
            const response = await handleGetRequest(endPoints.getallteams, true, false, dispatch);
            if (response?.data) {
                const options = response.data.map((team) => ({
                    label: team.name || t('Unknown Team'),
                    value: team._id || team.id
                }));
                setTeams([{ label: t('All Teams'), value: null }, ...options]);
            }
        };

        loadUsers();
        loadTeams();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, preferredLanguage]);

    const loadPerformance = async () => {
        const params = new URLSearchParams();
        if (filters.startDate) {
            params.append('startDate', filters.startDate.toISOString().split('T')[0]);
        }
        if (filters.endDate) {
            params.append('endDate', filters.endDate.toISOString().split('T')[0]);
        }
        if (filters.userId) {
            params.append('userId', filters.userId);
        }
        if (filters.teamId) {
            params.append('teamId', filters.teamId);
        }

        const queryParams = params.toString();
        const response = await handleGetRequest(endPoints.getPerformanceReports(queryParams), true, false, dispatch);

        if (response?.data) {
            const satisfaction = response.data.satisfaction || [];
            const responseByTeam = response.data.responseTimesByTeam || [];
            const technicianFaults = response.data.faultsByTechnician || [];

            // Satisfaction from complaints: year, month, avgScore, responses
            const mappedSatisfaction = satisfaction.map((item) => {
                const date = new Date(item.year, item.month - 1, 1);
                const monthLabel = date.toLocaleString(preferredLanguage === 'es' ? 'es-ES' : 'en-US', { month: 'short', year: 'numeric' });

                return {
                    month: monthLabel,
                    score: item.avgScore ?? 0,
                    responses: item.responses ?? 0
                };
            });
            setSatisfactionData(mappedSatisfaction);

            const chart = {
                labels: mappedSatisfaction.map((d) => d.month),
                datasets: [
                    {
                        label: t('Customer Satisfaction Index'),
                        data: mappedSatisfaction.map((d) => d.score),
                        fill: false,
                        borderColor: '#42A5F5',
                        tension: 0.4
                    }
                ]
            };
            setChartData(chart);

            // Response times by team: avgResponseMinutes → format using formatTimeFromMinutes
            const mappedResponseTimes = responseByTeam.map((item) => {
                const minutes =
                    typeof item.avgResponseMinutes === 'number' && !Number.isNaN(item.avgResponseMinutes)
                        ? item.avgResponseMinutes
                        : 0;

                return {
                    team: item.team || t('Unknown'),
                    avgResponseMinutes: minutes,
                    avgResponseTime: formatTimeFromMinutes(minutes),
                    totalCases: item.totalCases ?? 0,
                    resolvedCases: item.resolvedCases ?? 0
                };
            });
            setResponseTimes(mappedResponseTimes);

            // Faults addressed by technician from complaints
            const mappedFaultsByTechnician = technicianFaults.map((item) => ({
                technician: item.technician || t('Unknown'),
                totalFaults: item.totalFaults ?? 0,
                resolved: item.resolved ?? 0,
                pending: item.pending ?? 0
            }));
            setFaultsByTechnician(mappedFaultsByTechnician);
        }
    };

    useEffect(() => {
        loadPerformance();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preferredLanguage]);

    const applyFilters = () => {
        loadPerformance();
    };

    const resetFilters = () => {
        setFilters({
            startDate: null,
            endDate: null,
            userId: null,
            teamId: null
        });
        setTimeout(() => {
            loadPerformance();
        }, 0);
    };

    const chartOptions = {
        plugins: {
            legend: {
                labels: {
                    usePointStyle: true
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 5
            }
        }
    };

    // Calculate overall average response time in minutes
    const overallAvgResponseMinutes = responseTimes.length > 0
        ? responseTimes.reduce((sum, r) => sum + (r.avgResponseMinutes || 0), 0) / responseTimes.length
        : 0;

    return (
        <div>
            {/* Filters Section */}
            <div className="card mb-4">
                <h4 className="mb-3">{t('Filters')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Start Date')}</label>
                        <Calendar 
                            value={filters.startDate} 
                            onChange={(e) => setFilters({ ...filters, startDate: e.value })} 
                            dateFormat="yy-mm-dd" 
                            placeholder={t('Select Start Date')} 
                            showIcon 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('End Date')}</label>
                        <Calendar 
                            value={filters.endDate} 
                            onChange={(e) => setFilters({ ...filters, endDate: e.value })} 
                            dateFormat="yy-mm-dd" 
                            placeholder={t('Select End Date')} 
                            showIcon 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('User')}</label>
                        <Dropdown 
                            value={filters.userId} 
                            options={users} 
                            onChange={(e) => setFilters({ ...filters, userId: e.value })} 
                            placeholder={t('Select User')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Team')}</label>
                        <Dropdown 
                            value={filters.teamId} 
                            options={teams} 
                            onChange={(e) => setFilters({ ...filters, teamId: e.value })} 
                            placeholder={t('Select Team')} 
                            className="w-full" 
                        />
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <Button label={t('Apply Filters')} icon="pi pi-filter" onClick={applyFilters} className="p-button-primary" />
                    <Button label={t('Reset')} icon="pi pi-refresh" onClick={resetFilters} className="p-button-secondary" />
                </div>
            </div>

            <div className="grid mb-4">
                <div className="col-12">
                    <Card title={t('Customer Satisfaction Index')}>
                        <Chart type="line" data={chartData} options={chartOptions} />
                    </Card>
                </div>
            </div>

            <div className="grid mb-4">
                <div className="col-12 md:col-6">
                    <Card>
                        <h5 className="mb-3">{t('Average Satisfaction Score')}</h5>
                        <div className="text-3xl font-bold text-primary">
                            {satisfactionData.length > 0 
                                ? (satisfactionData.reduce((sum, d) => sum + d.score, 0) / satisfactionData.length).toFixed(2)
                                : '0.00'
                            } / 5.0
                        </div>
                        <p className="text-color-secondary mt-2">
                            {t('Based on')} {satisfactionData.reduce((sum, d) => sum + d.responses, 0)} {t('responses')}
                        </p>
                    </Card>
                </div>
                <div className="col-12 md:col-6">
                    <Card>
                        <h5 className="mb-3">{t('Average Response Time')}</h5>
                        <div className="text-3xl font-bold text-primary">
                            {formatTimeFromMinutes(overallAvgResponseMinutes)}
                        </div>
                        <p className="text-color-secondary mt-2">
                            {t('Across all teams')}
                        </p>
                    </Card>
                </div>
            </div>

            <div className="card mb-4">
                <div className="mb-3">
                    <h4>{t('Response Times by Team')}</h4>
                    <p className="text-color-secondary">{t('Average response time metrics grouped by team performance.')}</p>
                </div>
                <DataTable
                    value={responseTimes}
                    emptyMessage={t('No data available.')}
                    responsiveLayout="scroll"
                >
                    <Column field="team" header={t('Team')} />
                    <Column field="avgResponseTime" header={t('Average Response Time')} />
                    <Column field="totalCases" header={t('Total Cases')} />
                    <Column field="resolvedCases" header={t('Resolved Cases')} />
                </DataTable>
            </div>

            <div className="card">
                <div className="mb-3">
                    <h4>{t('Faults Addressed by Technician')}</h4>
                    <p className="text-color-secondary">{t('Individual technician performance metrics showing total faults, resolved, and pending cases.')}</p>
                </div>
                <DataTable
                    value={faultsByTechnician}
                    emptyMessage={t('No data available.')}
                    responsiveLayout="scroll"
                >
                    <Column field="technician" header={t('Technician')} />
                    <Column field="totalFaults" header={t('Total Faults')} />
                    <Column field="resolved" header={t('Resolved')} />
                    <Column field="pending" header={t('Pending')} />
                </DataTable>
            </div>
        </div>
    );
};

export default PerformanceReports;
